import { useMemo, useState, useEffect } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import {Filter, X, MapPin, Search, Briefcase, RefreshCw, Bell} from 'lucide-react';
import toast from "react-hot-toast";
import { useHead } from "@unhead/react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import JobCard from "@/components/JobCard";
import Breadcrumb, { type BreadcrumbItem } from "@/components/Breadcrumb";
import SiloDeepDive from "@/components/SiloDeepDive";
import JobFormModal from "@/components/JobFormModal";
import { jobs as staticJobs, CONTRACT_LABELS, ContractType, JobCategory, Job } from "@/data/jobs";
import { useAllJobs, useRecruiterJobs, recruiterJobToJob, type RecruiterJob, type NewRecruiterJob } from "@/hooks/useRecruiterJobs";
import { useFilledJobIds } from "@/hooks/useFilledJobIds";
import { useSeoPages } from "@/hooks/useSeoPages";
import { useAuth } from "@/hooks/useAuth";
import { useJobAlerts, type AlertCategory, type NewJobAlert } from "@/hooks/useJobAlerts";
import { trackJobAlertCreated } from "@/lib/analytics";
import type { SEOPage } from "@/data/seoPages";

// Ensemble d'IDs statiques pour déduplication
const STATIC_IDS = new Set(staticJobs.map((j) => j.id));

// ── Helpers de recherche ────────────────────────────────────────────────
// Normalisation accent-insensible + trim + lowercase pour fiabiliser les
// comparaisons (« Côte d'Azur » == « cote d'azur », « Île-de-France » == « ile de france »).
function normalize(s: string | undefined | null): string {
  if (!s) return "";
  return s.
  toString().
  normalize("NFD").
  replace(/[\u0300-\u036f]/g, "").
  toLowerCase().
  trim();
}

// Mapping ville/département (mots-clés) → région française.
// Utilisé pour que le filtre « lieu » matche aussi quand l'utilisateur
// sélectionne une région complète (overlay mobile) alors que les offres
// n'ont qu'une ville/département.
const REGION_KEYWORDS: Record<string, string[]> = {
  "ile-de-france": ["paris", "75", "77", "78", "91", "92", "93", "94", "95", "versailles", "nanterre", "creteil", "bobigny", "melun", "evry", "cergy"],
  "auvergne-rhone-alpes": ["lyon", "grenoble", "saint-etienne", "clermont-ferrand", "annecy", "chambery", "valence", "villefranche", "01", "03", "07", "15", "26", "38", "42", "43", "63", "69", "73", "74"],
  "provence-alpes-cote d'azur": ["marseille", "nice", "toulon", "aix-en-provence", "avignon", "cannes", "antibes", "frejus", "04", "05", "06", "13", "83", "84"],
  "nouvelle-aquitaine": ["bordeaux", "biarritz", "pau", "limoges", "poitiers", "la rochelle", "angouleme", "bayonne", "16", "17", "19", "23", "24", "33", "40", "47", "64", "79", "86", "87"],
  "occitanie": ["toulouse", "montpellier", "nimes", "perpignan", "beziers", "narbonne", "albi", "carcassonne", "09", "11", "12", "30", "31", "32", "34", "46", "48", "65", "66", "81", "82"],
  "hauts-de-france": ["lille", "amiens", "calais", "dunkerque", "roubaix", "tourcoing", "arras", "valenciennes", "02", "59", "60", "62", "80"],
  "grand est": ["strasbourg", "reims", "metz", "nancy", "mulhouse", "colmar", "troyes", "chalons", "08", "10", "51", "52", "54", "55", "57", "67", "68", "88"],
  "bretagne": ["rennes", "brest", "quimper", "lorient", "vannes", "saint-malo", "saint-brieuc", "22", "29", "35", "56"],
  "pays de la loire": ["nantes", "angers", "le mans", "saint-nazaire", "laval", "la roche-sur-yon", "cholet", "44", "49", "53", "72", "85"],
  "normandie": ["rouen", "caen", "le havre", "cherbourg", "evreux", "alencon", "dieppe", "14", "27", "50", "61", "76"],
  "bourgogne-franche-comte": ["dijon", "besancon", "belfort", "chalon-sur-saone", "auxerre", "nevers", "macon", "21", "25", "39", "58", "70", "71", "89", "90"],
  "centre-val de loire": ["orleans", "tours", "bourges", "blois", "chartres", "chateauroux", "18", "28", "36", "37", "41", "45"],
  "corse": ["ajaccio", "bastia", "corte", "porto-vecchio", "2a", "2b"],
  "outre-mer": ["guadeloupe", "martinique", "guyane", "reunion", "mayotte", "saint-pierre", "tahiti", "noumea", "971", "972", "973", "974", "976"],
  "national / multi-sites": ["national", "multi-sites", "france entiere", "toute la france"]
};

// Vérifie si une offre matche un terme de lieu (ville, département, ou région).
function locationMatches(job: {location?: string;department?: string;region?: string;}, locFilter: string): boolean {
  const target = normalize(locFilter);
  if (!target) return true;
  const jobLoc = normalize(job.location);
  const jobDep = normalize(job.department);
  const jobReg = normalize((job as {region?: string;}).region);
  // Match direct ville/département/région
  if (jobLoc.includes(target) || jobDep.includes(target) || jobReg && jobReg.includes(target)) {
    return true;
  }
  // Match indirect : le terme est une région connue → on cherche dans ses villes/départements
  const keywords = REGION_KEYWORDS[target];
  if (keywords) {
    return keywords.some((kw) => jobLoc.includes(kw) || jobDep.includes(kw));
  }
  return false;
}

/**
 * Détecte une catégorie pré-filtrée selon le pathname.
 * /bnssa/offres-emploi -> BNSSA (verrouillée)
 * /maitre-nageur-sauveteur/offres-emploi -> MNS (verrouillée)
 */
function detectSiloFromPath(pathname: string): JobCategory | null {
  if (pathname.startsWith("/bnssa/offres-emploi")) return "BNSSA";
  if (pathname.startsWith("/maitre-nageur-sauveteur/offres-emploi")) return "MNS";
  return null;
}

export default function Jobs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isRecruiter, isCandidate, isOrganisme, isAdmin } = useAuth();
  const { createAlert } = useJobAlerts();
  const { createJob } = useRecruiterJobs();
  const [creatingAlert, setCreatingAlert] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Ouverture du modal de dépôt d'annonce depuis le hero (CTA recruteur).
  const [jobModalOpen, setJobModalOpen] = useState(false);
  // Clé sessionStorage partagée avec Backoffice.tsx pour finaliser le dépôt
  // d'annonce après création / connexion du compte recruteur.
  const PENDING_JOB_DRAFT_KEY = "bnssa-mns:pending-job-draft";
  // Mobile : repli/dépli de la section Présentation (intro SEO) au tiers visible.
  // Le HTML complet reste dans le DOM (préservation SEO / accessibilité Google),
  // seul l'affichage CSS est tronqué via max-height + fade.
  const [introExpanded, setIntroExpanded] = useState(false);

  // Catégorie verrouillée par l'URL (BNSSA/MNS) si on est sur une route silo
  const lockedCategory = useMemo(() => detectSiloFromPath(pathname), [pathname]);

  const initialCategory = (lockedCategory ?? searchParams.get("categorie") as JobCategory ?? "") as string;
  const initialContract = searchParams.get("contrat") as ContractType || "";
  const initialLocation = searchParams.get("lieu") || "";
  const initialKeyword = searchParams.get("q") || "";

  const [category, setCategory] = useState<string>(initialCategory);
  const [contract, setContract] = useState<string>(initialContract);
  const [location, setLocation] = useState<string>(initialLocation);
  const [keyword, setKeyword] = useState<string>(initialKeyword);

  // Resynchronise la catégorie si l'utilisateur navigue d'une URL silo à une autre
  useEffect(() => {
    if (lockedCategory && category !== lockedCategory) {
      setCategory(lockedCategory);
    }
  }, [lockedCategory, category]);

  // Offres recruteurs depuis la base Lumi (uniquement validées par l'admin)
  const { jobs: dbJobs, loading: dbLoading } = useAllJobs();

  // Missions AE/Intérim « pourvues » (au moins une candidature retenue).
  const { filledJobIds } = useFilledJobIds();

  // ── Page SEO associée à la route silo (éditable depuis l'admin via SeoEditor)
  // Les pages /bnssa/offres-emploi et /maitre-nageur-sauveteur/offres-emploi
  // ont une fiche SEO dédiée (intro au-dessus des annonces, sections en-dessous).
  const { mergedPages } = useSeoPages();
  const seoPage = useMemo<SEOPage | null>(() => {
    if (!lockedCategory) return null;
    const targetPath =
    lockedCategory === "BNSSA" ? "/bnssa/offres-emploi" : "/maitre-nageur-sauveteur/offres-emploi";
    const merged = mergedPages.find((p) => p?.path === targetPath);
    return merged ?? null;
  }, [lockedCategory, mergedPages]);

  // ── Données structurées JSON-LD : extraction d'une FAQ depuis les sections SEO
  // On détecte automatiquement la section dont le titre commence par « FAQ »
  // et on en extrait les <h3>questions</h3> + <p>réponses</p> pour générer le FAQPage.
  const faqJsonLd = useMemo(() => {
    if (!seoPage?.sections) return null;
    const faqSection = seoPage.sections.find((s) =>
    /^faq\b/i.test((s.title || "").trim())
    );
    if (!faqSection?.content) return null;
    const html = faqSection.content;
    // Capture chaque paire <h3>...</h3> suivi du contenu jusqu'au prochain <h3> ou fin
    const regex = /<h3[^>]*>([\s\S]*?)<\/h3>([\s\S]*?)(?=<h3[^>]*>|$)/gi;
    const stripTags = (s: string) =>
    s.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
    const items: {q: string;a: string;}[] = [];
    let m: RegExpExecArray | null;
    while ((m = regex.exec(html)) !== null) {
      const q = stripTags(m[1]);
      const a = stripTags(m[2]);
      if (q && a) items.push({ q, a });
    }
    if (items.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a }
      }))
    };
  }, [seoPage]);

  // Fusion : offres DB validées en premier (logos déjà enrichis dans useAllJobs), puis offres statiques
  const allJobs = useMemo<(Job & {isFromDb?: boolean;})[]>(() => {
    const converted = dbJobs.
    filter((rj) => !STATIC_IDS.has(rj._id))
    // Filtre public : seules les offres approuvées sont visibles
    // (les anciennes offres sans validationStatus sont considérées comme approuvées par compatibilité)
    .filter((rj) => {
      const status = (rj as RecruiterJob & {validationStatus?: string;}).validationStatus;
      return !status || status === "approved";
    }).
    map((rj) => ({ ...recruiterJobToJob(rj as RecruiterJob), isFromDb: true as const }));
    return [...converted, ...staticJobs.map((j) => ({ ...j, isFromDb: false as const }))];
  }, [dbJobs]);

  // Sync state -> URL
  // NOTE: si la catégorie est déjà imposée par le slug (/bnssa/offres-emploi
  // ou /maitre-nageur-sauveteur/offres-emploi), on n'ajoute PAS ?categorie=...
  // dans l'URL pour éviter la redondance (ex: /bnssa/offres-emploi?categorie=BNSSA).
  useEffect(() => {
    const params: Record<string, string> = {};
    if (category && !lockedCategory) params.categorie = category;
    if (contract) params.contrat = contract;
    if (location) params.lieu = location;
    if (keyword) params.q = keyword;
    setSearchParams(params, { replace: true });
  }, [category, contract, location, keyword, lockedCategory, setSearchParams]);

  const filtered = useMemo(() => {
    return allJobs.
    filter((j) => {
      if (category && j.category !== category) return false;
      if (contract && j.contractType !== contract) return false;
      if (location) {
        // Filtre lieu intelligent : ville, département, région complète,
        // ou région via mapping (ex. « Provence-Alpes-Côte d'Azur » → Marseille, Nice…).
        if (!locationMatches(j, location)) return false;
      }
      if (keyword) {
        const kw = normalize(keyword);
        // Recherche élargie + accent-insensible : titre, employeur, description,
        // ville, département, catégorie. Permet de taper « marseille », « bnssa »,
        // « cote d azur », « 75 » etc. directement dans la barre de recherche.
        const haystack = [
        j.title,
        j.employer,
        j.description,
        j.location,
        j.department,
        j.category,
        (j as {region?: string;}).region].

        map(normalize).
        join(" ");
        if (!haystack.includes(kw)) return false;
      }
      return true;
    })
    // Marque les missions AE/Intérim pourvues (candidat retenu) pour affichage
    // du tampon « Mission pourvue » + blocage d'accès dans JobCard.
    .map((j) => (filledJobIds.has(String(j.id)) ? { ...j, isFilled: true } : j))
    // Tri chronologique inverse : plus récente d'abord
    .sort(
      (a, b) =>
      new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
    );
  }, [allJobs, category, contract, location, keyword, filledJobIds]);

  const reset = () => {
    setCategory("");
    setContract("");
    setLocation("");
    setKeyword("");
  };

  const activeFiltersCount = [category, contract, location, keyword].filter(Boolean).length;

  // ── CTA « Créer une alerte » : sauvegarde la recherche actuelle
  // Si l'utilisateur n'est pas connecté, on stocke un draft en sessionStorage
  // et on l'envoie sur /inscription puis sur /tableau-bord?tab=alertes.
  const buildAlertDraft = (): NewJobAlert => {
    const alertCategory: AlertCategory =
    lockedCategory ?? (category === "BNSSA" || category === "MNS" ? category as JobCategory : "ALL");
    const contractTypes: ContractType[] = contract ? [contract as ContractType] : [];
    const namePieces: string[] = [];
    if (keyword) namePieces.push(keyword);
    if (alertCategory !== "ALL") namePieces.push(alertCategory);
    if (contractTypes.length > 0) namePieces.push(CONTRACT_LABELS[contractTypes[0]]);
    if (location) namePieces.push(location);
    return {
      name: namePieces.length > 0 ? namePieces.join(" · ") : "Mon alerte personnalisée",
      category: alertCategory,
      contractTypes: contractTypes.length > 0 ? contractTypes : undefined,
      location: location || undefined,
      keywords: keyword || undefined,
      frequency: "weekly",
      isActive: true
    };
  };

  const handleCreateAlert = async () => {
    const draft = buildAlertDraft();
    // Pas connecté → stocker draft + redirection inscription
    if (!isAuthenticated || !user?.userId) {
      try {
        sessionStorage.setItem("bnssa-mns:job-alert-draft", JSON.stringify(draft));
        trackJobAlertCreated({ category: alertCategory, region: draft.region, frequency: draft.frequency, asGuest: true });
      } catch {

        // silent
      }toast("Connectez-vous pour finaliser votre alerte emploi.", { icon: "🔔" });
      navigate("/inscription?next=/tableau-bord?tab=alertes");
      return;
    }
    // Recruteur → fonction non destinée à cet espace
    if (isRecruiter) {
      toast.error("Les alertes emploi sont réservées aux candidats.");
      return;
    }
    // Connecté candidat → création directe + redirection vers le dashboard
    setCreatingAlert(true);
    try {
      await createAlert(user.userId, user.email, draft);
      toast.success("Alerte créée ! Vous serez notifié par email.");
      trackJobAlertCreated({ category: alertCategory, region: draft.region, frequency: draft.frequency, asGuest: false });
      navigate("/tableau-bord?tab=alertes");
    } catch {
      toast.error("Impossible de créer l'alerte. Réessayez.");
    } finally {
      setCreatingAlert(false);
    }
  };

  // ── Ouverture du modal de dépôt depuis le CTA recruteur du hero.
  // Garde de rôle : un candidat ou un organisme connecté ne peut pas déposer
  // une annonce. Les recruteurs connectés, les visiteurs non connectés et
  // l'admin restent autorisés.
  const handleOpenJobModal = () => {
    if (isAuthenticated && !isAdmin && (isCandidate || isOrganisme)) {
      toast.error(
        isOrganisme
          ? "Votre compte est un compte organisme de formation. Seuls les recruteurs peuvent publier une offre d'emploi."
          : "Votre compte est un compte candidat. Seuls les recruteurs peuvent publier une offre d'emploi."
      );
      return;
    }
    setJobModalOpen(true);
  };

  // ── CTA recruteur « Publier une offre » : ouvre directement le modal de dépôt
  // d'annonce (même formulaire que le back-office). À l'envoi :
  //  • recruteur/admin connecté → création directe puis redirection back-office ;
  //  • visiteur non connecté → on stocke l'annonce en brouillon (sessionStorage)
  //    et on l'envoie vers l'inscription recruteur ; le back-office finalise le
  //    dépôt automatiquement à la première connexion.
  const handleSaveJob = async (data: NewRecruiterJob) => {
    // Cas 1 : recruteur/admin connecté → création immédiate
    if (isAuthenticated && user?.userId && (isRecruiter || isAdmin)) {
      try {
        await createJob(data, user.userId, user.email, (user as { userName?: string })?.userName);
        setJobModalOpen(false);
        toast.success("Annonce envoyée ! Elle sera publiée après validation.");
        navigate("/backoffice");
      } catch {
        toast.error("Impossible d'envoyer l'annonce. Réessayez.");
      }
      return;
    }
    // Cas 2 : non connecté (ou compte non recruteur) → brouillon + inscription
    try {
      sessionStorage.setItem(PENDING_JOB_DRAFT_KEY, JSON.stringify(data));
    } catch {
      // silent
    }
    setJobModalOpen(false);
    toast("Créez votre compte recruteur pour finaliser le dépôt de votre annonce.", { icon: "📝" });
    navigate("/inscription?role=recruiter&next=/backoffice");
  };

  // ── Données structurées : ItemList de JobPosting (sur les routes silo BNSSA/MNS uniquement)
  // Limité à 10 offres pour rester léger et conforme aux bonnes pratiques Google for Jobs.
  const baseUrl = "https://bnssa-mns.fr";
  const jobPostingsJsonLd = useMemo(() => {
    if (!lockedCategory) return null;
    const top = filtered.slice(0, 10);
    if (top.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: top.map((job, idx) => {
        // Description HTML structurée (Google for Jobs recommande HTML).
        const rawDesc = job.description ||
        `Poste de ${job.category === "BNSSA" ? "surveillant aquatique BNSSA" : "Maître Nageur Sauveteur"} à ${job.location}.`;
        // On limite intelligemment à ~1500 chars en préservant l'enveloppe HTML.
        const safeDesc = rawDesc.length > 1500 ? rawDesc.slice(0, 1500) + "…" : rawDesc;
        const htmlDesc = /<\/?[a-z][\s\S]*>/i.test(safeDesc) ?
        safeDesc :
        `<p>${safeDesc.replace(/\n\s*\n/g, "</p><p>").replace(/\n/g, "<br/>")}</p>`;

        return {
          "@type": "ListItem",
          position: idx + 1,
          item: {
            "@type": "JobPosting",
            title: job.title,
            description: htmlDesc,
            datePosted: job.publishedAt,
            ...(job.deadline ? { validThrough: `${job.deadline}T23:59:59+01:00` } : {}),
            employmentType:
            job.contractType === "CDI" ?
            "FULL_TIME" :
            job.contractType === "CDD" || job.contractType === "CDD-saisonnier" ?
            "TEMPORARY" :
            job.contractType === "interim" ?
            "TEMPORARY" :
            job.contractType === "auto-entrepreneur" ?
            "CONTRACTOR" :
            job.contractType === "temps-partiel-CDI" || job.contractType === "temps-partiel-CDD" ?
            "PART_TIME" :
            "OTHER",
            // identifier : recommandé pour la déduplication.
            identifier: {
              "@type": "PropertyValue",
              name: job.employer,
              value: job.id
            },
            hiringOrganization: {
              "@type": "Organization",
              name: job.employer,
              sameAs: "https://bnssa-mns.fr",
              ...(job.logo ? { logo: job.logo } : {})
            },
            jobLocation: {
              "@type": "Place",
              address: {
                "@type": "PostalAddress",
                addressLocality: job.location,
                addressRegion: job.department,
                addressCountry: "FR"
              }
            },
            applicantLocationRequirements: {
              "@type": "Country",
              name: "FR"
            },
            // educationRequirements : certification professionnelle requise.
            educationRequirements: {
              "@type": "EducationalOccupationalCredential",
              credentialCategory: "professional certification",
              name: job.category === "BNSSA" ?
              "BNSSA — Brevet National de Sécurité et de Sauvetage Aquatique" :
              "BPJEPS AAN, BEESAN ou Licence STAPS"
            },
            experienceRequirements: {
              "@type": "OccupationalExperienceRequirements",
              monthsOfExperience: 0
            },
            occupationalCategory: job.category === "BNSSA" ?
            "G1204 — Surveillant de baignade BNSSA" :
            "G1204 — Maître Nageur Sauveteur",
            url: `${baseUrl}/offre/${job.id}`,
            industry: job.category === "BNSSA" ? "Surveillance aquatique" : "Activités aquatiques et natation",
            directApply: true
          }
        };
      })
    };
  }, [filtered, lockedCategory]);

  // Injection des données structurées JSON-LD via Unhead
  useHead({
    script: [
    ...(jobPostingsJsonLd ?
    [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify(jobPostingsJsonLd),
      key: "jobs-itemlist-jsonld"
    }] :

    []),
    ...(faqJsonLd ?
    [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify(faqJsonLd),
      key: "jobs-faq-jsonld"
    }] :

    [])]

  });

  return (
    <Layout>
      <Seo
        title={
        seoPage?.title ?
        seoPage.title :
        lockedCategory === "BNSSA" ?
        "Offres d'emploi BNSSA — Postes de Surveillant Aquatique | Emplois BNSSA & MNS" :
        lockedCategory === "MNS" ?
        "Offres d'emploi Maître Nageur Sauveteur — MNS en France | Emplois BNSSA & MNS" :
        category === "BNSSA" ?
        "Offres BNSSA — Surveillant Aquatique | Emplois BNSSA & MNS" :
        category === "MNS" ?
        "Offres Maître Nageur Sauveteur MNS | Emplois BNSSA & MNS" :
        "Offres d'emploi BNSSA & Maître Nageur Sauveteur — Recherche | Emplois BNSSA & MNS"
        }
        description={
        seoPage?.metaDescription ?
        seoPage.metaDescription :
        lockedCategory === "BNSSA" ?
        "Toutes les offres d'emploi BNSSA en France : CDI, CDD saisonnier, intérim, auto-entrepreneur. Surveillants aquatiques recherchés partout en France & DOM-TOM." :
        lockedCategory === "MNS" ?
        "Toutes les offres d'emploi Maître Nageur Sauveteur en France : CDI, CDD, titulaire FPT, auto-entrepreneur. Trouvez votre poste MNS sur bnssa-mns.fr." :
        "Offres d'emploi BNSSA et Maître Nageur Sauveteur en France. Filtrez par contrat (CDI, CDD, intérim, auto-entrepreneur) et par lieu. Postulez en ligne."
        }
        canonical={
        lockedCategory === "BNSSA" ?
        "https://bnssa-mns.fr/bnssa" :
        lockedCategory === "MNS" ?
        "https://bnssa-mns.fr/maitre-nageur-sauveteur" :
        "https://bnssa-mns.fr/offres"
        } />


      {/* Fil d'ariane (visible hors silo + dans silo BNSSA/MNS pour rattacher la page) */}
      {(() => {
        const breadcrumbItems: BreadcrumbItem[] =
        lockedCategory === "BNSSA" ?
        [
        { label: "BNSSA", href: "/bnssa" },
        { label: "Offres d'emploi BNSSA" }] :

        lockedCategory === "MNS" ?
        [
        { label: "Maître Nageur Sauveteur", href: "/maitre-nageur-sauveteur" },
        { label: "Offres d'emploi MNS" }] :

        [{ label: "Offres d'emploi" }];
        const accent: "red" | "blue" | "gray" =
        lockedCategory === "BNSSA" ? "red" : lockedCategory === "MNS" ? "blue" : "gray";
        return (
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
              <Breadcrumb items={breadcrumbItems} accent={accent} />
            </div>
          </div>);

      })()}

      {/* Header */}
      {/* Mobile : padding vertical réduit (py-5) pour remonter le contenu et   */}
      {/* faire apparaître la 1ʳᵉ annonce au-dessus de la ligne de flottaison.  */}
      {/* Desktop (sm+) : conserve un hero plus aéré (sm:py-12).                 */}
      <section className="bg-gradient-to-br from-gray-900 to-slate-800 text-white py-5 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-3">
            {seoPage?.h1 ?
            seoPage.h1 :
            `Offres d'emploi ${
            category === "BNSSA" ? "BNSSA" : category === "MNS" ? "MNS" : "BNSSA & MNS"}`
            }
          </h1>
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-gray-300 text-lg">
              {filtered.length} offre{filtered.length > 1 ? "s" : ""} correspondant à votre recherche
            </p>
            {dbLoading &&
            <span className="flex items-center gap-1.5 text-sm text-blue-300 animate-pulse">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                Chargement des offres recruteurs…
              </span>
            }
            {!dbLoading && dbJobs.length > 0 &&
            <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full font-semibold">
                +{dbJobs.length} offre{dbJobs.length > 1 ? "s" : ""} recruteur
              </span>
            }
          </div>

          {/* CTA recruteur : déposer une annonce. Cible le backoffice si le      */}
          {/* recruteur/admin est connecté, sinon l'inscription recruteur.        */}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-4 rounded-xl bg-white/10 border border-white/15 backdrop-blur-sm px-4 py-3">
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-red-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                <Briefcase className="w-4.5 h-4.5 text-white" />
              </span>
              <p className="text-sm text-gray-200">
                <span className="font-semibold text-white">Vous recrutez&nbsp;?</span>{" "}
                Publiez gratuitement votre offre BNSSA ou MNS.
              </p>
            </div>
            <button
              type="button"
              onClick={handleOpenJobModal}
              className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 hover:bg-gray-100 text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors shadow-sm flex-shrink-0">

              <Briefcase className="w-4 h-4" />
              Publier une offre
            </button>
          </div>

          {/* Search + Filters (compact, mobile-friendly) */}
          {/* Mobile : seul le bouton « Filtres » est visible en haut, les champs   */}
          {/* de recherche (mot-clé + lieu) sont déplacés dans le panneau dépliable.*/}
          {/* Desktop (sm+) : les deux champs restent visibles directement en haut. */}
          <div className="mt-4 sm:mt-6 bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              {/* Champs de recherche — desktop uniquement (sm+) */}
              <div className="hidden sm:flex flex-1 items-center gap-2 px-3 py-2 sm:border-r sm:border-gray-200">
                <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Métier, mot-clé, employeur..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="flex-1 outline-none text-gray-900 placeholder-gray-400 text-sm bg-transparent"
                  aria-label="Mot-clé" />

              </div>
              <div className="hidden sm:flex flex-1 items-center gap-2 px-3 py-2">
                <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Ville ou département"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="flex-1 outline-none text-gray-900 placeholder-gray-400 text-sm bg-transparent"
                  aria-label="Lieu" />

              </div>
              {/* Bouton Filtres — mobile uniquement, presque pleine largeur, hauteur compacte conservée */}
              <button
                onClick={() => setFiltersOpen(!filtersOpen)}
                className="sm:hidden mx-auto w-[95%] inline-flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-md font-semibold text-xs transition-colors"
                aria-expanded={filtersOpen}
                aria-controls="mobile-filters-panel">

                <Filter className="w-3.5 h-3.5" />
                {filtersOpen ? "Masquer" : "Rechercher & filtrer"}
                {activeFiltersCount > 0 &&
                <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-gray-900 text-white text-[10px] font-bold">
                    {activeFiltersCount}
                  </span>
                }
              </button>
            </div>

            {/* Panneau filtres mobile intégré à la barre de recherche (sm:hidden) */}
            {/* Inclut désormais les champs de recherche (mot-clé + lieu) en haut.   */}
            {filtersOpen &&
            <div
              id="mobile-filters-panel"
              className="sm:hidden border-t border-gray-200 bg-gray-50 p-4 space-y-5">

                <div className="flex items-center justify-between">
                  <h2 className="font-bold text-gray-900 flex items-center gap-2 text-sm">
                    <Filter className="w-4 h-4" />
                    Affiner la recherche
                  </h2>
                  {activeFiltersCount > 0 &&
                <button
                  onClick={reset}
                  className="text-xs text-red-600 hover:underline flex items-center gap-1 font-medium">

                      <X className="w-3 h-3" />
                      Réinitialiser
                    </button>
                }
                </div>

                {/* Champs de recherche (mobile) — au sommet du panneau */}
                <div className="space-y-2">
                  <h3 className="text-[11px] font-semibold text-gray-700 uppercase tracking-wider mb-2">
                    Recherche
                  </h3>
                  <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                    <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <input
                    type="text"
                    placeholder="Métier, mot-clé, employeur..."
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    className="flex-1 outline-none text-gray-900 placeholder-gray-400 text-sm bg-transparent"
                    aria-label="Mot-clé" />

                  </div>
                  <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                    <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <input
                    type="text"
                    placeholder="Ville ou département"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="flex-1 outline-none text-gray-900 placeholder-gray-400 text-sm bg-transparent"
                    aria-label="Lieu" />

                  </div>
                </div>

                {/* Catégorie */}
                <div>
                  <h3 className="text-[11px] font-semibold text-gray-700 uppercase tracking-wider mb-2">
                    Catégorie
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {[
                  { value: "", label: "Toutes" },
                  { value: "BNSSA", label: "BNSSA", color: "bg-red-500" },
                  { value: "MNS", label: "MNS", color: "bg-blue-500" }].
                  map((opt) => {
                    const disabled = lockedCategory !== null && opt.value !== lockedCategory;
                    const active = category === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => !disabled && setCategory(opt.value)}
                        disabled={disabled}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${
                        disabled ?
                        "opacity-40 cursor-not-allowed border-gray-200 bg-white text-gray-500" :
                        active ?
                        "bg-gray-900 text-white border-gray-900" :
                        "bg-white text-gray-700 border-gray-300 hover:border-gray-400"}`
                        }>

                          {opt.color && <span className={`w-2 h-2 rounded-full ${opt.color}`} />}
                          {opt.label}
                        </button>);

                  })}
                  </div>
                  {lockedCategory &&
                <p className="mt-2 text-xs text-gray-500 italic">
                      Filtre verrouillé par l'univers {lockedCategory}.
                    </p>
                }
                </div>

                {/* Type de contrat */}
                <div>
                  <h3 className="text-[11px] font-semibold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Briefcase className="w-3 h-3" />
                    Type de contrat
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                    type="button"
                    onClick={() => setContract("")}
                    className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${
                    contract === "" ?
                    "bg-gray-900 text-white border-gray-900" :
                    "bg-white text-gray-700 border-gray-300 hover:border-gray-400"}`
                    }>

                      Tous
                    </button>
                    {(Object.keys(CONTRACT_LABELS) as ContractType[]).map((c) => {
                    const active = contract === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setContract(c)}
                        className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${
                        active ?
                        "bg-gray-900 text-white border-gray-900" :
                        "bg-white text-gray-700 border-gray-300 hover:border-gray-400"}`
                        }>

                          {CONTRACT_LABELS[c]}
                        </button>);

                  })}
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      </section>

      {/* ─── INTRO SEO (au-dessus des annonces, éditable depuis l'admin) ────── */}
      {/* Bloc volontairement DISCRET : fond neutre, eyebrow gris, texte atténué.   */}
      {/* Repli au tiers sur mobile ET desktop (≈3 lignes) avec « Lire plus ».      */}
      {/* Le HTML complet reste dans le DOM (visible Google), seul l'affichage CSS  */}
      {/* est limité par max-height + fade.                                         */}
      {seoPage?.intro &&
      <section className="bg-gray-50/60 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-5">
            <div className="max-w-4xl">
              <span className="inline-block text-[10px] font-medium uppercase tracking-[0.16em] mb-1.5 text-gray-500">
                Présentation
              </span>
              <div className="relative">
                <div
                className={`seo-rich-content text-gray-600 leading-snug text-[13px] sm:text-sm ${
                lockedCategory === "BNSSA" ? "seo-accent-red" : "seo-accent-blue"} ${

                introExpanded ?
                "" :
                "max-h-[3.5rem] sm:max-h-[4.5rem] overflow-hidden"}`
                }
                dangerouslySetInnerHTML={{
                  __html: /<\/?[a-z][\s\S]*>/i.test(seoPage.intro) ?
                  seoPage.intro :
                  `<p>${seoPage.intro.
                  replace(/\n\s*\n/g, "</p><p>").
                  replace(/\n/g, "<br/>")}</p>`
                }} />

                {/* Fade gradient discret en bas du bloc tronqué */}
                {!introExpanded &&
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-gray-50/90 to-transparent" />

              }
              </div>
              {/* Bouton Lire plus / Lire moins — discret, gris */}
              <button
              type="button"
              onClick={() => setIntroExpanded((v) => !v)}
              className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 underline underline-offset-2 decoration-gray-300"
              aria-expanded={introExpanded}>

                {introExpanded ? "Lire moins" : "Lire plus"}
              </button>
            </div>
          </div>
        </section>
      }

      {/* Body */}
      {/* Mobile : padding vertical réduit (py-4) pour rapprocher la 1ʳᵉ annonce */}
      {/* du haut de page. Desktop (sm+) : aération conservée (sm:py-10).        */}
      <section className="py-4 sm:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
            {/* Filters Sidebar — desktop uniquement.                              */}
            {/* Sur mobile, les filtres sont intégrés à la barre de recherche       */}
            {/* en haut de page (panneau dépliable sous la barre).                  */}
            <aside className="hidden lg:block">
              <div className="bg-white border border-gray-200 rounded-xl p-5 sticky top-20">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-bold text-gray-900 flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    Filtres
                  </h2>
                  {activeFiltersCount > 0 &&
                  <button
                    onClick={reset}
                    className="text-xs text-red-600 hover:underline flex items-center gap-1">

                      <X className="w-3 h-3" />
                      Réinitialiser
                    </button>
                  }
                </div>

                {/* Catégorie */}
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">
                    Catégorie
                  </h3>
                  <div className="space-y-2">
                    {[
                    { value: "", label: "Toutes" },
                    { value: "BNSSA", label: "BNSSA", color: "bg-red-500" },
                    { value: "MNS", label: "MNS", color: "bg-blue-500" }].
                    map((opt) => {
                      const disabled = lockedCategory !== null && opt.value !== lockedCategory;
                      return (
                        <label
                          key={opt.value}
                          className={`flex items-center gap-2 text-sm ${
                          disabled ?
                          "cursor-not-allowed opacity-40" :
                          "cursor-pointer"}`
                          }>

                          <input
                            type="radio"
                            name="category"
                            checked={category === opt.value}
                            onChange={() => setCategory(opt.value)}
                            disabled={disabled}
                            className="text-blue-600" />

                          {opt.color && <span className={`w-2 h-2 rounded-full ${opt.color}`}></span>}
                          <span className="text-gray-700">{opt.label}</span>
                        </label>);

                    })}
                  </div>
                  {lockedCategory &&
                  <p className="mt-2 text-xs text-gray-500 italic">
                      Filtre verrouillé par l'univers {lockedCategory}.
                    </p>
                  }
                </div>

                {/* Type de contrat */}
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Briefcase className="w-3.5 h-3.5" />
                    Type de contrat
                  </h3>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="radio"
                        name="contract"
                        checked={contract === ""}
                        onChange={() => setContract("")}
                        className="text-blue-600" />

                      <span className="text-gray-700">Tous les contrats</span>
                    </label>
                    {(Object.keys(CONTRACT_LABELS) as ContractType[]).map((c) =>
                    <label
                      key={c}
                      className="flex items-center gap-2 cursor-pointer text-sm">

                        <input
                        type="radio"
                        name="contract"
                        checked={contract === c}
                        onChange={() => setContract(c)}
                        className="text-blue-600" />

                        <span className="text-gray-700">{CONTRACT_LABELS[c]}</span>
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </aside>

            {/* Results */}
            <div>
              {filtered.length === 0 ?
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                  <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Aucune offre trouvée
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Essayez d'élargir vos critères de recherche.
                  </p>
                  <button
                  onClick={reset}
                  className="text-sm text-blue-600 hover:underline font-medium">

                    Réinitialiser les filtres
                  </button>
                </div> :

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {filtered.map((job) =>
                <JobCard key={job.id} job={job} />
                )}
                </div>
              }

              {/* CTA « Créer une alerte emploi » — déplacé EN DESSOUS de la liste */}
              {/* des annonces. Visible quand au moins un filtre est actif et qu'il   */}
              {/* existe au moins une offre, pour proposer de sauvegarder la         */}
              {/* recherche en cours après que l'utilisateur a parcouru les annonces.*/}
              {activeFiltersCount > 0 && filtered.length > 0 &&
              <div
                className={`mt-6 rounded-xl border p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 ${
                lockedCategory === "BNSSA" ?
                "bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 border-red-200" :
                lockedCategory === "MNS" ?
                "bg-gradient-to-br from-amber-50 via-sky-50 to-blue-50 border-blue-200" :
                "bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200"}`
                }>

                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-amber-500 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Bell className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-sm sm:text-base font-bold text-gray-900">
                        Recevez les nouvelles offres par email
                      </h2>
                      <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                        Créez une alerte avec les critères actuels et soyez prévenu dès qu'une nouvelle offre est publiée.
                      </p>
                    </div>
                  </div>
                  <button
                  type="button"
                  onClick={handleCreateAlert}
                  disabled={creatingAlert}
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors shadow-sm flex-shrink-0">

                    <Bell className="w-4 h-4" />
                    {creatingAlert ? "Création…" : "Créer une alerte"}
                  </button>
                </div>
              }
            </div>
          </div>
        </div>
      </section>
      {/* ─── BLOC SEO (en dessous des annonces, éditable depuis l'admin) ───── */}
      {seoPage?.sections && seoPage.sections.length > 0 &&
      <section
        className={`py-12 sm:py-14 border-t ${
        lockedCategory === "BNSSA" ?
        "bg-red-50/40 border-red-100" :
        "bg-blue-50/40 border-blue-100"}`
        }>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <span
              className={`inline-block text-[11px] font-bold uppercase tracking-[0.14em] mb-3 ${
              lockedCategory === "BNSSA" ? "text-red-700" : "text-blue-700"}`
              }>

                En savoir plus
              </span>
              <h2
              className="text-2xl sm:text-3xl font-black text-gray-900 tracking-[-0.02em] mb-8"
              style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>

                {seoPage.keyword ?
              `Tout savoir sur les ${seoPage.keyword}` :
              `Informations sur l'emploi ${lockedCategory}`}
              </h2>
              <article className="prose prose-lg max-w-none">
                {seoPage.sections.map((section, i) => {
                const raw = section.content || "";
                const isHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
                const html = isHtml ?
                raw :
                `<p>${raw.
                replace(/\n\s*\n/g, "</p><p>").
                replace(/\n/g, "<br/>")}</p>`;
                return (
                  <div key={i} className="mb-10">
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span
                        className={`w-1 h-7 rounded-full inline-block flex-shrink-0 ${
                        lockedCategory === "BNSSA" ? "bg-red-600" : "bg-blue-600"}`
                        } />

                        {section.title}
                      </h3>
                      <div
                      className={`seo-rich-content text-gray-700 leading-relaxed text-base ${
                      lockedCategory === "BNSSA" ? "seo-accent-red" : "seo-accent-blue"}`
                      }
                      dangerouslySetInnerHTML={{ __html: html }} />

                    </div>);

              })}
              </article>
            </div>
          </div>
        </section>
      }

      {/* ─── APPROFONDIR LE GUIDE (silo BNSSA / MNS uniquement) ─────────────── */}
      {lockedCategory &&
      <SiloDeepDive
        silo={lockedCategory}
        currentPath={
        lockedCategory === "BNSSA" ?
        "/bnssa/offres-emploi" :
        "/maitre-nageur-sauveteur/offres-emploi"
        } />

      }

      {/* Modal de dépôt d'annonce ouvert depuis le CTA recruteur du hero. */}
      {jobModalOpen &&
      <JobFormModal
        existing={null}
        onClose={() => setJobModalOpen(false)}
        onSave={handleSaveJob}
        submitLabel={
        isAuthenticated && (isRecruiter || isAdmin) ?
        "Envoyer l'annonce" :
        "Continuer"
        } />

      }
    </Layout>);

}